// Toast notification for authentication errors
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

export default function AuthErrorToast() {
  const { authError, clearAuthError } = useAuth();

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        clearAuthError();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [authError, clearAuthError]);

  return (
    <AnimatePresence>
      {authError && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.95 }}
          transition={{ duration: 0.3, type: 'spring', stiffness: 200, damping: 20 }}
          className="fixed top-4 right-4 left-4 md:left-auto md:w-96 z-[100]"
        >
          <div className="bg-red-500/10 backdrop-blur-xl border-2 border-red-500/30 rounded-2xl shadow-2xl overflow-hidden">
            {/* Animated gradient border */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 via-red-600/20 to-red-500/20 animate-pulse pointer-events-none" />
            
            <div className="relative p-4 flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-red-300 mb-1">
                  Sign In Failed
                </h3>
                <p className="text-sm text-red-200/90 leading-relaxed">
                  {authError}
                </p>
                
                {/* Retry suggestion */}
                <p className="text-xs text-red-300/70 mt-2">
                  💡 Tip: If popups are blocked, check your browser settings and allow popups for this site.
                </p>
              </div>
              
              {/* Close button */}
              <button
                onClick={clearAuthError}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-red-500/20 transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="w-5 h-5 text-red-300" />
              </button>
            </div>
            
            {/* Progress bar for auto-dismiss */}
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 8, ease: 'linear' }}
              className="h-1 bg-gradient-to-r from-red-500 to-red-600"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
